/**
 * Smart genealogical date parser with GEDCOM qualifier support.
 *
 * Detects the format of each date field and preserves qualifiers
 * (ABT, BEF, AFT, BET...AND) in the display.
 */

import type { CardLevel } from '../types';

export interface ParsedDate {
  raw: string;
  format: 'iso' | 'gedcom-exact' | 'gedcom-qualified' | 'gedcom-range' | 'year-only' | 'unknown';
  year: number | null;
  month: number | null;
  day: number | null;
  qualifier: 'about' | 'before' | 'after' | 'between' | null;
  yearEnd: number | null;
}

const GEDCOM_MONTHS: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

/**
 * Parse any date string into a structured object.
 * Supports: ISO (YYYY-MM-DD), GEDCOM exact (9 NOV 1841),
 * GEDCOM qualified (ABT 1850, BEF 1900, AFT 1800, circa 1825),
 * GEDCOM range (BET 1820 AND 1830), year-only (1841).
 */
export function parseDate(dateStr?: string): ParsedDate | null {
  if (!dateStr || !dateStr.trim()) return null;
  const raw = dateStr.trim();
  // #1503 (NC #749) — in de database staan ook datums als "3 april 1900" en
  // "19-05-1897" (import + oudere invoer). Herken die door ze eerst naar
  // GEDCOM-notatie te vertalen; anders valt alles hieronder door naar de
  // jaar-fallback en toont de tijdlijn alleen het jaartal. `raw` in het
  // resultaat blijft bewust de oorspronkelijke tekst.
  const s = normalizeDateNotation(raw);

  // 1. ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return {
      raw,
      format: 'iso',
      year: parseInt(isoMatch[1]),
      month: parseInt(isoMatch[2]),
      day: parseInt(isoMatch[3]),
      qualifier: null,
      yearEnd: null,
    };
  }

  // 2. GEDCOM range: BET(WEEN) YYYY AND YYYY  or  BET DD MMM YYYY AND DD MMM YYYY
  const rangeMatch = s.match(/^BET(?:WEEN)?\s+(.+?)\s+AND\s+(.+)/i);
  if (rangeMatch) {
    const startParsed = parseDate(rangeMatch[1]);
    const endParsed = parseDate(rangeMatch[2]);
    return {
      raw,
      format: 'gedcom-range',
      year: startParsed?.year ?? null,
      month: startParsed?.month ?? null,
      day: startParsed?.day ?? null,
      qualifier: 'between',
      yearEnd: endParsed?.year ?? null,
    };
  }

  // NC #810 — GEDCOM-periode: FROM … TO … (een periode, als een range), FROM … (open
  // einde, als "na") en TO … (als "voor"). Zo blijft een leeftijd erop onbepaald.
  const periode = s.match(/^(?:FROM\s+(.+?)(?:\s+TO\s+(.+))?|TO\s+(.+))$/i);
  if (periode) {
    const [, van, tot, alleenTot] = periode;
    const begin = parseDate(van || alleenTot);
    const eind = van && tot ? parseDate(tot) : null;
    return {
      raw,
      format: van && tot ? 'gedcom-range' : 'gedcom-qualified',
      year: begin?.year ?? null,
      month: begin?.month ?? null,
      day: begin?.day ?? null,
      qualifier: van && tot ? 'between' : van ? 'after' : 'before',
      yearEnd: eind?.year ?? null,
    };
  }

  // 3. GEDCOM qualified: ABT/BEF/AFT/circa/ca./~ + date
  const qualMatch = s.match(
    /^(ABT\.?|ABOUT|CIRCA|CA\.?|~|ONG\.?|VOOR|BEFORE|BEF\.?|NA|AFTER|AFT\.?)\s+(.+)/i
  );
  if (qualMatch) {
    const qualWord = qualMatch[1].toUpperCase().replace(/\.$/, '');
    let qualifier: ParsedDate['qualifier'] = 'about';
    if (['BEF', 'BEFORE', 'VOOR'].includes(qualWord)) qualifier = 'before';
    else if (['AFT', 'AFTER', 'NA'].includes(qualWord)) qualifier = 'after';

    const inner = parseDate(qualMatch[2]);
    if (inner) {
      return { ...inner, raw, format: 'gedcom-qualified', qualifier };
    }
    const yearFallback = qualMatch[2].match(/\b(\d{4})\b/);
    return {
      raw,
      format: 'gedcom-qualified',
      year: yearFallback ? parseInt(yearFallback[1]) : null,
      month: null,
      day: null,
      qualifier,
      yearEnd: null,
    };
  }

  // 4. GEDCOM exact: DD MMM YYYY
  const gedcomFull = s.match(/^(\d{1,2})\s+([A-Z]{3})\s+(\d{4})$/i);
  if (gedcomFull) {
    return {
      raw,
      format: 'gedcom-exact',
      year: parseInt(gedcomFull[3]),
      month: GEDCOM_MONTHS[gedcomFull[2].toUpperCase()] || null,
      day: parseInt(gedcomFull[1]),
      qualifier: null,
      yearEnd: null,
    };
  }

  // 5. GEDCOM month+year: MMM YYYY
  const gedcomMonthYear = s.match(/^([A-Z]{3})\s+(\d{4})$/i);
  if (gedcomMonthYear) {
    return {
      raw,
      format: 'gedcom-exact',
      year: parseInt(gedcomMonthYear[2]),
      month: GEDCOM_MONTHS[gedcomMonthYear[1].toUpperCase()] || null,
      day: null,
      qualifier: null,
      yearEnd: null,
    };
  }

  // 6. Year only: YYYY
  const yearOnly = s.match(/^(\d{4})$/);
  if (yearOnly) {
    return {
      raw,
      format: 'year-only',
      year: parseInt(yearOnly[1]),
      month: null,
      day: null,
      qualifier: null,
      yearEnd: null,
    };
  }

  // 7. Fallback: find any 4-digit number
  const fallbackYear = s.match(/\b(\d{4})\b/);
  return {
    raw,
    format: 'unknown',
    year: fallbackYear ? parseInt(fallbackYear[1]) : null,
    month: null,
    day: null,
    qualifier: null,
    yearEnd: null,
  };
}

/**
 * Format a date for short display in tree nodes.
 * Shows year with qualifier prefix: "1841", "~1850", "<1900", ">1800", "1820–1830"
 */
export function formatYearForTree(dateStr?: string): string | null {
  const parsed = parseDate(dateStr);
  if (!parsed || !parsed.year) return null;

  switch (parsed.qualifier) {
    case 'about':
      return `~${parsed.year}`;
    case 'before':
      return `<${parsed.year}`;
    case 'after':
      return `>${parsed.year}`;
    case 'between':
      return `${parsed.year}\u2013${parsed.yearEnd || '?'}`;
    default:
      return `${parsed.year}`;
  }
}

/**
 * #748: Display-year voor person-cards met fallback naar doop/begrafenis.
 * Retourneert het jaartal volgens `formatYearForTree`-regels en een vlag
 * die aangeeft of dit een afgeleide waarde is (isFallback=true).
 *
 * Volgt NGV/Gramps-conventie: doopdatum is proxy voor geboorte wanneer
 * BIRT ontbreekt maar BAPM/CHR bekend is. UI markeert afgeleide jaartallen
 * visueel (cursief), maar `baptismDate` wordt NIET als `birthDate` opgeslagen
 * — GEDCOM-export blijft zuiver.
 */
export interface DisplayYear {
  year: string | null;
  isFallback: boolean;
  sourceField: 'birth' | 'baptism' | 'death' | 'burial' | null;
}

export function getDisplayBirthYear(birthDate?: string, baptismDate?: string): DisplayYear {
  const birth = formatYearForTree(birthDate);
  if (birth) return { year: birth, isFallback: false, sourceField: 'birth' };
  const baptism = formatYearForTree(baptismDate);
  if (baptism) return { year: baptism, isFallback: true, sourceField: 'baptism' };
  return { year: null, isFallback: false, sourceField: null };
}

export function getDisplayDeathYear(deathDate?: string, burialDate?: string): DisplayYear {
  const death = formatYearForTree(deathDate);
  if (death) return { year: death, isFallback: false, sourceField: 'death' };
  const burial = formatYearForTree(burialDate);
  if (burial) return { year: burial, isFallback: true, sourceField: 'burial' };
  return { year: null, isFallback: false, sourceField: null };
}

const MONTH_ABBR_NL = [
  'JAN',
  'FEB',
  'MRT',
  'APR',
  'MEI',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OKT',
  'NOV',
  'DEC',
];
const MONTH_ABBR_EN = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Split a date string for event-row display into two lines.
 *
 * Output (Refs Nextcloud #541, #543):
 *   exact:                  line1="21 AUG"        line2="1920"
 *   month+year:             line1="AUG"           line2="1920"
 *   year-only:              line1=""              line2="1920"
 *   qualifier+year:         line1="ONG"|"VOOR"|"NA"  line2="1920"
 *   qualifier+day/month:    line1="NA 12 MEI"     line2="1905"  (#543: prefix
 *                           werd vroeger overschreven door dag/maand-tak)
 *   between (year-only):    line1="TUSSEN"        line2="1820–1830"
 *   between (with month):   line1="TUSSEN"        line2="DEC 1904 – MEI 1905"
 *                           (#541: was line1="DEC", line2="1904-1905" — leek
 *                           op één maand voor beide jaren)
 *
 * Returns null if no year could be parsed (caller hides the column).
 */
export function formatEventDateTwoLine(
  dateStr: string | undefined,
  lang: string = 'en'
): { line1: string; line2: string } | null {
  const parsed = parseDate(dateStr);
  if (!parsed || !parsed.year) return null;

  const months = lang === 'nl' ? MONTH_ABBR_NL : MONTH_ABBR_EN;
  const qualLabel = (q: 'about' | 'before' | 'after' | null): string => {
    if (q === 'about') return lang === 'nl' ? 'ONG' : 'ABT';
    if (q === 'before') return lang === 'nl' ? 'VOOR' : 'BEF';
    if (q === 'after') return lang === 'nl' ? 'NA' : 'AFT';
    return '';
  };

  // #541 — Between gets its own dedicated formatting (full month-info on line2)
  if (parsed.qualifier === 'between' && parsed.yearEnd) {
    const line1 = lang === 'nl' ? 'TUSSEN' : 'BETW';
    // Reach into raw GEDCOM range for end-month (parseDate stores yearEnd only).
    // Format start + end with month-abbrev when the original raw contained months.
    const rangeMatch = (parsed.raw || '').match(/^BET(?:WEEN)?\s+(.+?)\s+AND\s+(.+)/i);
    const startInner = rangeMatch ? parseDate(rangeMatch[1]) : null;
    const endInner = rangeMatch ? parseDate(rangeMatch[2]) : null;
    const fmt = (p: ReturnType<typeof parseDate>): string => {
      if (!p || !p.year) return '?';
      if (p.month) return `${months[p.month - 1]} ${p.year}`;
      return String(p.year);
    };
    if (startInner?.month || endInner?.month) {
      return { line1, line2: `${fmt(startInner)} – ${fmt(endInner)}` };
    }
    return { line1, line2: `${parsed.year}–${parsed.yearEnd}` };
  }

  // #543 — Date part on line1 (day+month / month / qualifier-only)
  let datePart = '';
  if (parsed.day && parsed.month) {
    datePart = `${parsed.day} ${months[parsed.month - 1]}`;
  } else if (parsed.month) {
    datePart = months[parsed.month - 1];
  }

  const qual = qualLabel(parsed.qualifier as 'about' | 'before' | 'after' | null);
  // Always show qualifier when present (was previously dropped if day/month set).
  const line1 = qual && datePart ? `${qual} ${datePart}` : qual || datePart;
  const line2 = String(parsed.year);

  return { line1, line2 };
}

/**
 * Extract just the year number from any date format.
 * Backward-compatible replacement for the old extractYear.
 */
export function extractYear(dateStr?: string): string | null {
  const parsed = parseDate(dateStr);
  return parsed?.year ? String(parsed.year) : null;
}

/**
 * Extract just the year as a number from any date format.
 * Returns null if no year could be parsed.
 */
export function extractYearNum(dateStr?: string): number | null {
  const parsed = parseDate(dateStr);
  return parsed?.year ?? null;
}

/**
 * NC #836/#814 — jaartal voor de 110-jaarregel; spiegel van `people_levend_jaartal`
 * (migratie 193). Behoudend: bij "BET x AND y" telt de bovengrens, en "AFT x"
 * bewijst niets (wie ná 1915 geboren is kan nog leven).
 */
function levendJaartal(datum?: string): number | null {
  const p = parseDate(datum);
  if (!p || p.qualifier === 'after') return null;
  return p.qualifier === 'between' ? p.yearEnd : p.year;
}

/**
 * #1833 — het geboortejaar voor elke levensgrensregel (automatisch overleden én de fout
 * "kan niet meer leven"): behoudend zoals de DB-trigger, met de doop als de geboortedatum
 * geen jaartal heeft. Eén lezing, zodat een fout nooit blokkeert waar niets de persoon op
 * overleden zet ("AFT 1848").
 */
export function levendGeboortejaar(birthDate?: string, baptismDate?: string): number | null {
  return levendJaartal(birthDate) ?? levendJaartal(baptismDate);
}

/** Geboren (of bij ontbreken gedoopt) meer dan 110 jaar vóór dit jaar — dezelfde regel als de DB-trigger. */
export function ouderDanLevensgrens(birthDate?: string, baptismDate?: string): boolean {
  const jaar = levendGeboortejaar(birthDate, baptismDate);
  return jaar !== null && jaar < new Date().getFullYear() - 110;
}

/**
 * Mag de weergave "Levend" / een leeftijd tot vandaag tonen? De DB-vlag alleen is
 * niet genoeg: de 110-jaarregel (migratie 193) vuurt pas bij een schrijfactie, dus
 * wie in 1916 geboren is staat in 2027 nog als levend tot iemand hem opslaat.
 * Heeft de genealoog de status zelf vastgelegd (`isLivingHandmatig`, migratie 197),
 * dan geldt de 110-jaarregel niet.
 */
export function geldtAlsLevend(p: {
  isLiving?: boolean;
  isLivingHandmatig?: boolean;
  birthDate?: string;
  baptismDate?: string;
  deathDate?: string;
  burialDate?: string;
}): boolean {
  return (
    p.isLiving === true &&
    !p.deathDate &&
    !p.burialDate &&
    (p.isLivingHandmatig === true || !ouderDanLevensgrens(p.birthDate, p.baptismDate))
  );
}

/**
 * Format a GEDCOM date for human display with full month names.
 * Converts "16 DEC 1838" → "16 december 1838" (nl) or "16 December 1838" (en).
 * Preserves qualifiers: "ABT 16 DEC 1838" → "ABT 16 december 1838".
 */
export const MONTH_NAMES: Record<string, Record<string, string>> = {
  nl: {
    JAN: 'januari',
    FEB: 'februari',
    MAR: 'maart',
    APR: 'april',
    MAY: 'mei',
    JUN: 'juni',
    JUL: 'juli',
    AUG: 'augustus',
    SEP: 'september',
    OCT: 'oktober',
    NOV: 'november',
    DEC: 'december',
  },
  en: {
    JAN: 'January',
    FEB: 'February',
    MAR: 'March',
    APR: 'April',
    MAY: 'May',
    JUN: 'June',
    JUL: 'July',
    AUG: 'August',
    SEP: 'September',
    OCT: 'October',
    NOV: 'November',
    DEC: 'December',
  },
};

const QUALIFIER_LABELS: Record<string, Record<string, string>> = {
  nl: {
    ABT: 'circa',
    ABOUT: 'circa',
    CIRCA: 'circa',
    BEF: 'voor',
    BEFORE: 'voor',
    AFT: 'na',
    AFTER: 'na',
    EST: 'geschat',
    BET: 'tussen',
    BETWEEN: 'tussen',
    AND: 'en',
    FROM: 'vanaf',
    TO: 'tot',
  },
  en: {
    ABT: 'circa',
    ABOUT: 'circa',
    CIRCA: 'circa',
    BEF: 'before',
    BEFORE: 'before',
    AFT: 'after',
    AFTER: 'after',
    EST: 'estimated',
    BET: 'between',
    BETWEEN: 'between',
    AND: 'and',
    FROM: 'from',
    TO: 'to',
  },
};

/**
 * #1880 — datum in cijfers, zoals de akteregel de aktedatum toont: "08-01-1934",
 * "01-1934" of "1934", met de benadering in de taal van de gebruiker ("circa 1850",
 * "voor 03-02-1850", "tussen 1820 en 1830"). `alleenJaar` laat dag en maand weg.
 * Onleesbare tekst komt ongewijzigd terug: geen verzonnen datum.
 */
export function formatDatumCijfers(
  dateStr: string | undefined,
  lang: string = 'nl',
  alleenJaar = false
): string {
  if (!dateStr || !dateStr.trim()) return '';
  const quals = QUALIFIER_LABELS[lang] || QUALIFIER_LABELS.en;
  const s = normalizeDateNotation(dateStr.trim());
  const range = s.match(/^BET(?:WEEN)?\s+(.+?)\s+AND\s+(.+)$/i);
  if (range) {
    return `${quals.BET} ${formatDatumCijfers(range[1], lang, alleenJaar)} ${quals.AND} ${formatDatumCijfers(range[2], lang, alleenJaar)}`;
  }
  const periode = s.match(/^FROM\s+(.+?)\s+TO\s+(.+)$/i);
  if (periode) {
    return `${quals.FROM} ${formatDatumCijfers(periode[1], lang, alleenJaar)} ${quals.TO} ${formatDatumCijfers(periode[2], lang, alleenJaar)}`;
  }
  const geschat = s.match(/^(?:EST|CAL)\.?\s+(.+)$/i);
  if (geschat) return `${quals.EST} ${formatDatumCijfers(geschat[1], lang, alleenJaar)}`;
  const p = parseDate(s);
  if (!p || !p.year || p.format === 'unknown') return dateStr.trim();
  const twee = (n: number) => String(n).padStart(2, '0');
  const kern =
    alleenJaar || !p.month
      ? String(p.year)
      : p.day
        ? `${twee(p.day)}-${twee(p.month)}-${p.year}`
        : `${twee(p.month)}-${p.year}`;
  const voor =
    p.qualifier === 'about'
      ? quals.ABT
      : p.qualifier === 'before'
        ? quals.BEF
        : p.qualifier === 'after'
          ? quals.AFT
          : '';
  return voor ? `${voor} ${kern}` : kern;
}

/**
 * Format a GEDCOM date for human display with full month names and translated qualifiers.
 * "BET 1955 AND 1974" → "tussen 1955 en 1974" (nl) / "between 1955 and 1974" (en)
 * "AFT 21 AUG 2012" → "na 21 augustus 2012" (nl) / "after 21 August 2012" (en)
 * "22 DEC 1921" → "22 december 1921" (nl) / "22 December 1921" (en)
 */
export function formatDateDisplay(dateStr: string | undefined, lang: string = 'en'): string {
  if (!dateStr) return '';
  const months = MONTH_NAMES[lang] || MONTH_NAMES.en;
  const monthList = Object.values(months);
  const quals = QUALIFIER_LABELS[lang] || QUALIFIER_LABELS.en;

  // Handle ISO dates (YYYY-MM-DD)
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const day = parseInt(isoMatch[3]);
    const monthIdx = parseInt(isoMatch[2]) - 1;
    const year = isoMatch[1];
    if (monthIdx >= 0 && monthIdx < 12) {
      return `${day} ${monthList[monthIdx]} ${year}`;
    }
  }
  // Handle partial ISO dates (YYYY-MM)
  const isoMonthMatch = dateStr.match(/^(\d{4})-(\d{2})$/);
  if (isoMonthMatch) {
    const monthIdx = parseInt(isoMonthMatch[2]) - 1;
    const year = isoMonthMatch[1];
    if (monthIdx >= 0 && monthIdx < 12) {
      return `${monthList[monthIdx]} ${year}`;
    }
  }

  let result = dateStr;
  // Replace qualifiers
  result = result.replace(/\bBET(?:WEEN)?\b/gi, quals.BET);
  result = result.replace(/\bAND\b/g, quals.AND);
  result = result.replace(/\bAFT(?:ER)?\b/gi, quals.AFT);
  result = result.replace(/\bBEF(?:ORE)?\b/gi, quals.BEF);
  result = result.replace(/\bABT\.?\b/gi, quals.ABT);
  result = result.replace(/\bABOUT\b/gi, quals.ABT);
  result = result.replace(/\bCIRCA\b/gi, quals.ABT);
  result = result.replace(/\bEST(?:IMATED)?\b/gi, quals.EST);
  result = result.replace(/\bFROM\b/gi, quals.FROM);
  result = result.replace(/\bTO\b/gi, quals.TO);
  // #1519 — '02 MAR 1916' → '2 maart 1916', net als de ISO-tak hierboven (parseInt).
  result = result.replace(
    /\b0([1-9])(?=\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b)/gi,
    '$1'
  );
  // Replace month abbreviations
  result = result.replace(
    /\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/gi,
    (match) => months[match.toUpperCase()] || match
  );
  return result;
}

const GEDCOM_MONTH_CODES = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Auto-format compact date input to GEDCOM notation on blur.
 * Supports:
 *   01081952  → 1 AUG 1952     (DDMMYYYY)
 *   1081952   → 1 AUG 1952     (DMMYYYY)
 *   01-08-1952 → 1 AUG 1952    (DD-MM-YYYY)
 *   01/08/1952 → 1 AUG 1952    (DD/MM/YYYY)
 *   01.08.1952 → 1 AUG 1952    (DD.MM.YYYY)
 *   1-8-1952  → 1 AUG 1952     (D-M-YYYY)
 *   081952    → AUG 1952        (MMYYYY, no day)
 *   08-1952   → AUG 1952        (MM-YYYY)
 *   1952      → 1952            (year only, unchanged)
 *
 * Already valid GEDCOM dates (e.g. "1 AUG 1952") are returned unchanged.
 * European date order (DD-MM-YYYY) is assumed — standard in genealogy.
 */
export function autoFormatDate(input: string): string {
  if (!input) return input;
  const trimmed = input.trim();

  // Already contains a GEDCOM month code → already formatted
  if (/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/i.test(trimmed)) return trimmed;

  // DD-MM-YYYY or DD/MM/YYYY or DD.MM.YYYY (with separators)
  const sepMatch = trimmed.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (sepMatch) {
    const d = parseInt(sepMatch[1]);
    const m = parseInt(sepMatch[2]);
    const y = parseInt(sepMatch[3]);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${d} ${GEDCOM_MONTH_CODES[m - 1]} ${y}`;
    }
  }

  // MM-YYYY or MM/YYYY (month + year with separator, no day)
  const sepMonthYear = trimmed.match(/^(\d{1,2})[\/\-.](\d{4})$/);
  if (sepMonthYear) {
    const m = parseInt(sepMonthYear[1]);
    const y = parseInt(sepMonthYear[2]);
    if (m >= 1 && m <= 12) {
      return `${GEDCOM_MONTH_CODES[m - 1]} ${y}`;
    }
  }

  // Pure digits — no separators
  const digits = trimmed.replace(/\D/g, '');
  if (digits === trimmed && digits.length >= 6) {
    // DDMMYYYY (8 digits)
    if (digits.length === 8) {
      const d = parseInt(digits.substring(0, 2));
      const m = parseInt(digits.substring(2, 4));
      const y = parseInt(digits.substring(4, 8));
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${d} ${GEDCOM_MONTH_CODES[m - 1]} ${y}`;
      }
    }
    // DMMYYYY (7 digits: single-digit day)
    if (digits.length === 7) {
      const d = parseInt(digits.substring(0, 1));
      const m = parseInt(digits.substring(1, 3));
      const y = parseInt(digits.substring(3, 7));
      if (m >= 1 && m <= 12 && d >= 1 && d <= 9) {
        return `${d} ${GEDCOM_MONTH_CODES[m - 1]} ${y}`;
      }
    }
    // MMYYYY (6 digits: month + year, no day)
    if (digits.length === 6) {
      const m = parseInt(digits.substring(0, 2));
      const y = parseInt(digits.substring(2, 6));
      if (m >= 1 && m <= 12) {
        return `${GEDCOM_MONTH_CODES[m - 1]} ${y}`;
      }
    }
  }

  // No match — return as-is (year only, free text, etc.)
  return trimmed;
}

// #626: Korte maandafkortingen per taal voor invoer/weergave in datum-inputs.
// GEDCOM-opslag blijft ALTIJD Engels (standaard), maar de UI toont lokale vorm.
const SHORT_MONTH_ABBR: Record<string, string[]> = {
  nl: ['JAN', 'FEB', 'MRT', 'APR', 'MEI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEC'],
  en: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
};

// Reverse-map Nederlandse korte afkortingen → GEDCOM (Engels)
const NL_TO_GEDCOM_MONTH: Record<string, string> = {
  MRT: 'MAR',
  MEI: 'MAY',
  OKT: 'OCT',
};

/**
 * #626: Format een GEDCOM datum voor weergave in een text-input.
 * GEDCOM blijft canonical (31 MAY 1905), UI toont lokale afkorting (31 MEI 1905 in NL).
 * Qualifiers (ABT, BEF, etc.) blijven onveranderd — alleen maandafkortingen worden vertaald.
 */
export function formatGedcomDateForInput(input: string | undefined, lang: string = 'en'): string {
  if (!input) return '';
  if (lang === 'en') return input; // GEDCOM is al Engels
  const abbrs = SHORT_MONTH_ABBR[lang];
  if (!abbrs) return input;
  return input.replace(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/gi, (match) => {
    const idx = GEDCOM_MONTH_CODES.indexOf(match.toUpperCase());
    return idx >= 0 ? abbrs[idx] : match;
  });
}

// #781 (Task 8): full month-name → 3-letter GEDCOM code mapping.
// Beslaat zowel Nederlands als Engels. #1503 vulde de ontbrekende Engelse
// namen aan (may/june/july) — zonder die bleef "3 May 1900" onvertaald.
const FULL_MONTH_TO_GEDCOM: Record<string, string> = {
  // Nederlands
  januari: 'JAN',
  februari: 'FEB',
  maart: 'MAR',
  april: 'APR',
  mei: 'MAY',
  juni: 'JUN',
  juli: 'JUL',
  augustus: 'AUG',
  september: 'SEP',
  oktober: 'OCT',
  november: 'NOV',
  december: 'DEC',
  // Engels (april identiek aan NL; september/november/december idem)
  january: 'JAN',
  february: 'FEB',
  march: 'MAR',
  may: 'MAY',
  june: 'JUN',
  july: 'JUL',
  august: 'AUG',
  october: 'OCT',
};

// Eén bron voor de maandnaam-regex: een naam die in de mapping staat, wordt ook
// herkend. De `\b` aan het eind voorkomt dat "august" wint van "augustus".
const FULL_MONTH_PATTERN = new RegExp(
  `\\b(${Object.keys(FULL_MONTH_TO_GEDCOM).join('|')})\\b`,
  'gi'
);

/**
 * #626: Normaliseer een localized-input datum terug naar GEDCOM.
 * Accepteert zowel "31 MEI 1905" (NL) als "31 MAY 1905" (EN) en retourneert altijd GEDCOM.
 *
 * Bewust ALLEEN de drie korte NL-afkortingen: deze functie hangt aan de
 * `onChange` van een controlled input en draait dus op elke toetsaanslag. Zou
 * hij volledige maandnamen omzetten, dan kaapt hij het veld halverwege een
 * woord — "12 august" → "12 AUG", waarna verder typen "12 AUGus 1888" oplevert
 * (elke maandnaam die prefix is van een langere heeft dat). De volledige namen
 * doet `normalizeDateNotation` op blur (#1503, fixronde 1).
 */
export function normalizeLocalizedDate(input: string): string {
  if (!input) return input;
  return input.replace(/\b(MRT|MEI|OKT)\b/gi, (match) => {
    return NL_TO_GEDCOM_MONTH[match.toUpperCase()] || match;
  });
}

/**
 * ISO (`YYYY-MM-DD`, eventueel met tijd) of GEDCOM (`D MMM YYYY`) — de opslagnorm.
 *
 * De maandcodes staan hier voluit en NIET als `[A-Z]{3}`: 'MEI', 'MRT' en 'OKT'
 * zien er hetzelfde uit maar worden door `normalizeLocalizedDate` wél omgezet
 * (naar MAY/MAR/OCT). Die mogen de snelle afslag dus niet nemen.
 */
const OPSLAGNORM =
  /^(?:\d{4}-\d{2}-\d{2}|\d{1,2} (?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC) \d{4}$)/;

/** De GEDCOM-kwalificaties die vooraan mogen staan; gelijk aan migratie 185. */
const QUALIFIER_PREFIX =
  /^((?:ABOUT|ABT|CIRCA|CAL|CA|BEFORE|BEF|AFTER|AFT|EST|ONG|VOOR|NA)\.?\s+)(.+)$/i;

/**
 * #1503: breng een datum-string naar GEDCOM-notatie (`D MMM YYYY`) zónder te parsen.
 * Vertaalt volledige maandnamen (NL+EN, hoofdletterongevoelig), de korte
 * NL-afkortingen en separator-notaties (DD-MM-YYYY, DD/MM/YYYY, DDMMYYYY) via
 * `autoFormatDate`. Wat niet exact op een van die vormen past, komt ongewijzigd
 * terug.
 *
 * Een kwalificatie vooraan (ABT/BEF/VOOR/…) blijft letterlijk staan en het deel
 * erachter wordt wél genormaliseerd — "VOOR 19-05-1897" → "VOOR 19 MAY 1897".
 *
 * Roept bewust GEEN parseDate aan — parseDate gebruikt juist deze functie.
 * Migratie `185_dates_dutch_month_names.sql` doet dezelfde transformatie in SQL.
 */
export function normalizeDateNotation(input: string): string {
  if (!input) return input;
  const trimmed = input.trim();
  // Drukke pad: staat de datum al in de opslagnorm (ISO of GEDCOM), dan geeft de
  // keten hieronder hem gegarandeerd ongewijzigd terug — `autoFormatDate` stopt
  // meteen op een GEDCOM-maandcode en valt voor ISO door naar `return trimmed`.
  // Elke kaart, tijdlijn en export loopt hier overheen, dus die ronde overslaan
  // scheelt het meeste werk.
  if (OPSLAGNORM.test(trimmed)) return trimmed;
  const qual = trimmed.match(QUALIFIER_PREFIX);
  const kern = qual ? qual[2] : trimmed;
  const metCodes = kern.replace(
    FULL_MONTH_PATTERN,
    (match) => FULL_MONTH_TO_GEDCOM[match.toLowerCase()] || match
  );
  const genormaliseerd = autoFormatDate(normalizeLocalizedDate(metCodes));
  return qual ? `${qual[1]}${genormaliseerd}` : genormaliseerd;
}

/**
 * Vergelijkt twee datum-strings semantisch (ongevoelig voor notatie).
 * - 'equal'      → exact dezelfde datum (eventueel beide leeg)
 * - 'compatible' → één is preciezer, beide kloppen (year-only vs DD-MM-YYYY)
 *                  of een modifier (circa/before/after) met overeenkomstig jaar
 * - 'mismatch'   → verschillende datums of één leeg + ander gevuld
 */
export function compareDates(
  a: string | null | undefined,
  b: string | null | undefined
): 'equal' | 'compatible' | 'mismatch' {
  const aTrim = (a || '').trim();
  const bTrim = (b || '').trim();
  if (!aTrim && !bTrim) return 'equal';
  if (!aTrim || !bTrim) return 'mismatch';

  // #1503 — de voormalige `normalizeForCompare` zit nu in parseDate zelf
  // (`normalizeDateNotation`), zodat het hele platform dezelfde notaties kent
  // en niet alleen deze vergelijking.
  const parsedA = parseDate(aTrim);
  const parsedB = parseDate(bTrim);

  if (!parsedA || !parsedB) return 'mismatch';
  if (parsedA.year == null || parsedB.year == null) return 'mismatch';

  // Vergelijk jaar
  if (parsedA.year !== parsedB.year) return 'mismatch';

  const aIsExact = parsedA.qualifier === null;
  const bIsExact = parsedB.qualifier === null;

  // Beide hebben month?
  if (parsedA.month != null && parsedB.month != null) {
    if (parsedA.month !== parsedB.month) return 'mismatch';
    if (parsedA.day != null && parsedB.day != null) {
      if (parsedA.day !== parsedB.day) return 'mismatch';
      // Volledige match
      if (aIsExact && bIsExact) return 'equal';
      return 'compatible';
    }
    // Eén van de twee mist day
    return 'compatible';
  }

  // Eén of beide hebben alleen jaar
  if (aIsExact && bIsExact && parsedA.month == null && parsedB.month == null) {
    return 'equal';
  }
  return 'compatible';
}

// ── Export-redesign (#525) ─────────────────────────────────────────────────
// formatDateForCard: niveau-bewuste datum-formatter voor persoon-kaarten.

const NL_MONTHS_SHORT = [
  'jan',
  'feb',
  'mrt',
  'apr',
  'mei',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
];

const GEDCOM_PREFIX_MAP: Record<string, (year: string) => string> = {
  ABT: (y) => `ca. ${y}`,
  BEF: (y) => `<${y}`,
  AFT: (y) => `>${y}`,
  CAL: (y) => `${y} (ber.)`,
  EST: (y) => `~${y}`,
};

function parseGedcomPrefix(date: string): { prefix: string | null; rest: string } {
  const m = date.match(/^(ABT|BEF|AFT|CAL|EST)\s+(.+)$/);
  if (m) return { prefix: m[1], rest: m[2] };
  return { prefix: null, rest: date };
}

function extractYearStr(date: string): string | null {
  const m = date.match(/(\d{4})/);
  return m ? m[1] : null;
}

function parseIsoDate(date: string): { year: string; month?: string; day?: string } | null {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return { year: m[1], month: m[2], day: m[3] };
  const ym = date.match(/^(\d{4})$/);
  if (ym) return { year: ym[1] };
  return null;
}

/**
 * Formatteer een datum-string voor een persoon-kaart in een specifiek
 * density-niveau. GEDCOM-prefixen (ABT/BEF/AFT/CAL/EST) communiceren
 * onzekerheid en worden altijd als jaar-only weergegeven met passend symbool:
 *
 *   ABT 1938 → "ca. 1938"
 *   BEF 1938 → "<1938"
 *   AFT 1938 → ">1938"
 *   CAL 1938 → "1938 (ber.)"
 *   EST 1938 → "~1938"
 *
 * Voor reguliere ISO-datums:
 *   detailed → "26 sep 1938"
 *   standard → "sep 1938"
 *   compact/micro → "1938"
 */
export function formatDateForCard(
  date: string,
  level: CardLevel,
  lang: 'nl' | 'en' = 'nl'
): string {
  // lang reserved voor toekomstige EN-vertaling (NL-MONTHS_SHORT is huidige set).
  void lang;
  if (!date) return '';
  // #561 — BET…AND / FROM…TO ranges: splits + format elke kant op het gevraagde niveau.
  const betRange = date.match(/^BET(?:WEEN)?\s+(.+?)\s+AND\s+(.+)$/i);
  if (betRange)
    return `${formatDateForCard(betRange[1], level, lang)} – ${formatDateForCard(betRange[2], level, lang)}`;
  const fromToRange = date.match(/^FROM\s+(.+?)\s+TO\s+(.+)$/i);
  if (fromToRange)
    return `${formatDateForCard(fromToRange[1], level, lang)} – ${formatDateForCard(fromToRange[2], level, lang)}`;
  const { prefix, rest } = parseGedcomPrefix(date);

  if (prefix) {
    const year = extractYearStr(rest);
    if (year && GEDCOM_PREFIX_MAP[prefix]) return GEDCOM_PREFIX_MAP[prefix](year);
  }

  const parsed = parseIsoDate(rest);
  if (!parsed) {
    // Niet-ISO format — probeer GEDCOM (DD MMM YYYY / MMM YYYY / YYYY).
    // Audit P2.2: voorheen werd hier de raw caps-string teruggegeven
    // ("8 MAY 1814"), wat per generatie verschilde tussen Nederlands en
    // Engelse caps. Normaliseer nu naar één consistente NL representatie.
    const gedcomMatch = rest.match(
      /^(\d{1,2})\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{4})$/i
    );
    if (gedcomMatch) {
      const day = parseInt(gedcomMatch[1], 10);
      const monthCode = gedcomMatch[2].toUpperCase();
      const year = gedcomMatch[3];
      const monthIdx = GEDCOM_MONTH_CODES.indexOf(monthCode);
      if (monthIdx >= 0) {
        const monthName = NL_MONTHS_SHORT[monthIdx];
        if (level === 'compact' || level === 'micro') return year;
        if (level === 'standard') return `${monthName} ${year}`;
        return `${day} ${monthName} ${year}`;
      }
    }
    const gedcomMonthYear = rest.match(
      /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{4})$/i
    );
    if (gedcomMonthYear) {
      const monthCode = gedcomMonthYear[1].toUpperCase();
      const year = gedcomMonthYear[2];
      const monthIdx = GEDCOM_MONTH_CODES.indexOf(monthCode);
      if (monthIdx >= 0) {
        const monthName = NL_MONTHS_SHORT[monthIdx];
        if (level === 'compact' || level === 'micro') return year;
        return `${monthName} ${year}`;
      }
    }
    const yearOnly = rest.match(/^(\d{4})$/);
    if (yearOnly) return yearOnly[1];
    return rest;
  }

  if (level === 'compact' || level === 'micro') return parsed.year;
  if (level === 'standard') {
    if (!parsed.month) return parsed.year;
    const monthName = NL_MONTHS_SHORT[parseInt(parsed.month, 10) - 1] ?? '';
    return `${monthName} ${parsed.year}`;
  }
  // detailed
  if (parsed.day && parsed.month) {
    const monthName = NL_MONTHS_SHORT[parseInt(parsed.month, 10) - 1] ?? '';
    return `${parseInt(parsed.day, 10)} ${monthName} ${parsed.year}`;
  }
  return parsed.year;
}

/** Compact dash-format `DD-mmm-YYYY` voor PDF-card vital-regels naast `°`/`†` iconen.
 * Voorbeelden: `12-feb-1840`, `feb-1840` (zonder dag), `1840` (alleen jaar).
 *
 * #525 retest v6: GEDCOM-onzekerheids-prefixes (ABT/CAL/EST/BEF/AFT) krijgen
 * Geneanet-conventie symbolen vóór de datum:
 *   ABT/CAL/EST → `~ ` (cirka)
 *   BEF        → `< ` (voor)
 *   AFT        → `> ` (na) */
export function formatDateDDMMM(date: string): string {
  if (!date) return '';
  // #561 — BET…AND / FROM…TO ranges: splits + format elke kant compact (kaart heeft
  // weinig ruimte → "1896 – 1906" i.p.v. de lange "tussen … en"-tekst). Voorheen
  // matchte geen enkele date-parser de range → rauwe GEDCOM-tekst op de kaart.
  const betRange = date.match(/^BET(?:WEEN)?\s+(.+?)\s+AND\s+(.+)$/i);
  if (betRange) return `${formatDateDDMMM(betRange[1])} – ${formatDateDDMMM(betRange[2])}`;
  const fromToRange = date.match(/^FROM\s+(.+?)\s+TO\s+(.+)$/i);
  if (fromToRange) return `${formatDateDDMMM(fromToRange[1])} – ${formatDateDDMMM(fromToRange[2])}`;
  const { prefix, rest } = parseGedcomPrefix(date);
  const prefixSymbol =
    prefix === 'ABT' || prefix === 'CAL' || prefix === 'EST'
      ? '~ '
      : prefix === 'BEF'
        ? '< '
        : prefix === 'AFT'
          ? '> '
          : '';

  const formatCore = (s: string): string => {
    const parsed = parseIsoDate(s);
    if (parsed) {
      if (parsed.day && parsed.month) {
        const day = parseInt(parsed.day, 10).toString().padStart(2, '0');
        const monthName = NL_MONTHS_SHORT[parseInt(parsed.month, 10) - 1] ?? '';
        return `${day}-${monthName}-${parsed.year}`;
      }
      if (parsed.month) {
        const monthName = NL_MONTHS_SHORT[parseInt(parsed.month, 10) - 1] ?? '';
        return `${monthName}-${parsed.year}`;
      }
      return parsed.year;
    }
    const gedcomMatch = s.match(
      /^(\d{1,2})\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{4})$/i
    );
    if (gedcomMatch) {
      const day = parseInt(gedcomMatch[1], 10).toString().padStart(2, '0');
      const monthIdx = GEDCOM_MONTH_CODES.indexOf(gedcomMatch[2].toUpperCase());
      const monthName = monthIdx >= 0 ? NL_MONTHS_SHORT[monthIdx] : gedcomMatch[2].toLowerCase();
      return `${day}-${monthName}-${gedcomMatch[3]}`;
    }
    const monthYearMatch = s.match(
      /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{4})$/i
    );
    if (monthYearMatch) {
      const monthIdx = GEDCOM_MONTH_CODES.indexOf(monthYearMatch[1].toUpperCase());
      const monthName = monthIdx >= 0 ? NL_MONTHS_SHORT[monthIdx] : monthYearMatch[1].toLowerCase();
      return `${monthName}-${monthYearMatch[2]}`;
    }
    const yearOnly = s.match(/^(\d{4})$/);
    if (yearOnly) return yearOnly[1];
    return s;
  };

  return `${prefixSymbol}${formatCore(rest)}`;
}

/**
 * Format a Date object as a Dutch-locale date string: "DD maand JJJJ".
 * Used by PDF header/footer for "Gegenereerd op" text (Plan B §10.1).
 */
export function formatDutchDate(date: Date): string {
  const months = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Vergelijk twee genealogische datums op {jaar, maand, dag}.
 *
 * A-09 — de kwaliteitscontrole keek uitsluitend naar het jaartal. Een typefout in
 * een DTB-transcriptie (overlijden "3 JAN 1850", geboorte "5 JUN 1850") passeerde
 * daardoor zonder melding, terwijl doop-na-geboorte binnen hetzelfde jaar juist
 * het meest voorkomende Nederlandse scenario is.
 *
 * Vergelijkt alleen op de delen die BEIDE datums hebben. "1850" tegenover
 * "3 JAN 1850" levert 0 en dus geen melding: we weten niet welke eerder viel, en
 * een tegenstrijdigheid melden die er niet is, is erger dan hem laten lopen.
 *
 * Spiegelt `compareGenealogicalDates` in `shared/validate.js` (server). Twee
 * implementaties omdat de server CommonJS is en dit de TS-laag; het gedrag moet
 * gelijk blijven.
 *
 * @returns -1 als a eerder is, 1 als a later is, 0 bij gelijk of niet te
 *   onderscheiden, null wanneer een van beide geen jaartal heeft.
 */
export function compareGenealogicalDates(a?: string, b?: string): -1 | 0 | 1 | null {
  const pa = parseDate(a);
  const pb = parseDate(b);
  if (!pa?.year || !pb?.year) return null;
  if (pa.year !== pb.year) return pa.year < pb.year ? -1 : 1;

  if (pa.month == null || pb.month == null) return 0;
  if (pa.month !== pb.month) return pa.month < pb.month ? -1 : 1;

  if (pa.day == null || pb.day == null) return 0;
  if (pa.day !== pb.day) return pa.day < pb.day ? -1 : 1;
  return 0;
}

/**
 * #1523 — sorteervergelijker voor lijsten: chronologisch (niet alfabetisch op de
 * ruwe string, waar '12 MAR 1850' vóór '3 JAN 1700' viel), en een datum zonder
 * jaartal staat in beide richtingen onderaan (B-36).
 */
/**
 * #1926 — twee exacte datums (dag, maand, jaar, geen "omstreeks") die minstens `jaren` jaar
 * uiteenlopen. Bij geboortedata: twee verschillende mensen, hoe goed naam en ouders kloppen.
 */
export function exacteDatumsJarenUiteen(a?: string | null, b?: string | null, jaren = 2): boolean {
  const pa = parseDate(a || undefined);
  const pb = parseDate(b || undefined);
  const exact = (d: ParsedDate | null): d is ParsedDate & { year: number } =>
    !!d && d.year != null && !!d.day && !!d.month && !d.qualifier;
  return exact(pa) && exact(pb) && Math.abs(pa.year - pb.year) >= jaren;
}

export function compareDatesUnknownLast(
  a: string | undefined,
  b: string | undefined,
  richting: 1 | -1
): number {
  const ka = extractYearNum(a) != null;
  const kb = extractYearNum(b) != null;
  if (!ka || !kb) return ka === kb ? 0 : ka ? -1 : 1;
  return richting * (compareGenealogicalDates(a, b) ?? 0);
}

/**
 * Volledige leeftijd in jaren tussen twee genealogische datums.
 *
 * B-29 — dit werd op twee plekken los berekend, allebei als kaal `sterfjaar -
 * geboortejaar`. Geboren 20-12-1800 en overleden 05-01-1850 werd dan 50 jaar in
 * plaats van 49. Systematisch een half jaar te hoog, en het verschuift kinderen
 * naar een hogere leeftijdsbucket — precies waar zuigelingensterfte-analyse
 * gevoelig voor is. De precisie was gewoon beschikbaar: 164 personen hebben een
 * volledige geboortedatum en 189 een sterfdatum.
 *
 * Dag-precies zodra BEIDE datums een maand hebben; anders het jaarverschil, want
 * dan is meer precisie een verzinsel. Een genealogische datum is vaak "1802" of
 * "ABT 1802", dus die terugval is de regel en niet de uitzondering.
 *
 * @returns de leeftijd in hele jaren, of null bij ontbrekende, onparseerbare of
 *   onlogische data (overlijden vóór geboorte).
 */
/**
 * Maakt deze kwalificatie de leeftijd onbepaald? (NC #715)
 *
 * `na 1920` zegt niet wanneer iemand stierf, alleen dat het daarna was. Het
 * verschil met de geboortedatum is dan een ondergrens, geen leeftijd. Hetzelfde
 * geldt voor `voor` (bovengrens) en een periode `tussen … en …`.
 *
 * `circa` telt wél: een genealoog die "ca. 1850" noteert doet een schatting, en
 * de leeftijd die daaruit volgt is diezelfde schatting — niet een ander soort
 * bewering.
 */
export function maaktLeeftijdOnbepaald(datum: ParsedDate | null): boolean {
  return (
    datum?.qualifier === 'after' || datum?.qualifier === 'before' || datum?.qualifier === 'between'
  );
}

/**
 * De leeftijd als ondergrens, bovengrens of exact getal (NC #715).
 *
 * Bestaat naast `ageInFullYears`, dat bij een open datum niets meer teruggeeft:
 * "minimaal 54" is voor een genealoog bruikbare informatie, een verzonnen "54"
 * is dat niet.
 */
export function ageBounded(
  birthDate?: string | null,
  deathDate?: string | null
): { years: number; bound: 'exact' | 'atLeast' | 'atMost' } | null {
  const geboorte = parseDate(birthDate ?? undefined);
  const overlijden = parseDate(deathDate ?? undefined);
  if (!geboorte?.year || !overlijden?.year) return null;

  const jaren = ruweLeeftijd(geboorte, overlijden);
  if (jaren === null) return null;

  // Later overlijden of eerdere geboorte dan genoteerd ⇒ de leeftijd kan alleen
  // hoger uitvallen; andersom alleen lager.
  const ondergrens = overlijden.qualifier === 'after' || geboorte.qualifier === 'before';
  const bovengrens = overlijden.qualifier === 'before' || geboorte.qualifier === 'after';
  if (ondergrens && !bovengrens) return { years: jaren, bound: 'atLeast' };
  if (bovengrens && !ondergrens) return { years: jaren, bound: 'atMost' };
  if (maaktLeeftijdOnbepaald(geboorte) || maaktLeeftijdOnbepaald(overlijden)) return null;
  return { years: jaren, bound: 'exact' };
}

function ruweLeeftijd(geboorte: ParsedDate, overlijden: ParsedDate): number | null {
  let jaren = (overlijden.year as number) - (geboorte.year as number);

  // Verjaardag nog niet gehad in het sterfjaar? Dan een jaar eraf.
  if (geboorte.month != null && overlijden.month != null) {
    const voorVerjaardag =
      overlijden.month < geboorte.month ||
      (overlijden.month === geboorte.month &&
        geboorte.day != null &&
        overlijden.day != null &&
        overlijden.day < geboorte.day);
    if (voorVerjaardag) jaren -= 1;
  }

  if (jaren < 0 || jaren > 130) return null;
  return jaren;
}

export function ageInFullYears(
  birthDate?: string | null,
  deathDate?: string | null
): number | null {
  const geboorte = parseDate(birthDate ?? undefined);
  const overlijden = parseDate(deathDate ?? undefined);
  if (!geboorte?.year || !overlijden?.year) return null;

  // NC #715 — "na 1920" als sterfdatum leverde gewoon 1920 op, en dus een
  // leeftijd die niemand heeft nagerekend. Een open einde geeft geen exacte
  // leeftijd; wie er tóch iets mee wil, gebruikt `ageBounded`.
  if (maaktLeeftijdOnbepaald(geboorte) || maaktLeeftijdOnbepaald(overlijden)) return null;

  let jaren = overlijden.year - geboorte.year;

  // Verjaardag nog niet gehad in het sterfjaar? Dan een jaar eraf.
  if (geboorte.month != null && overlijden.month != null) {
    const voorVerjaardag =
      overlijden.month < geboorte.month ||
      (overlijden.month === geboorte.month &&
        geboorte.day != null &&
        overlijden.day != null &&
        overlijden.day < geboorte.day);
    if (voorVerjaardag) jaren -= 1;
  }

  if (jaren < 0 || jaren > 130) return null;
  return jaren;
}

// PA-voorstel v5 (#1962) — de kalenderdatum in Nederland, gedeeld met de server.
export { datumInNederland } from '../../shared/datumInNederland.mjs';
